import React from 'react';
import PropTypes from 'prop-types';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import track from '@helper/tracking';

import articleJSONLD from './json-ld/articleJSONLD';
import eventJSONLD from './json-ld/eventJSONLD';

import TemplateDefault from '../../templates/default/default';

import Accordion from '../../organisms/accordion/accordion';
import Author from '../../molecules/author/author';
import Button from '../../atoms/button/button';
import Container from '../../layout/container/container';
import ContentBlocks from '../../organisms/content-blocks/contentBlocks.withRedux';
import Stage from '../../organisms/stage/stage';
import TagList from '../../molecules/tag-list/tagList.withRedux';
import TeaserContainer from '../../organisms/teaser-container/teaserContainer';
import CrossTeasers from '../../organisms/cross-teasers/crossTeasers.tsx';

const Entry = ({
  data = {
    articleAuthor: null,
    typeTags: null,
    widgetUrl: null,
    shopUrl: null,
    crossTeaserContainer: null,
    furtherContentsTeaser: null,
    thinkingSpaces: null,
    registerable: false,
    bookable: false,
    seatsAvailable: 0,
    status: ''
  }
}) => {
  const {
    type,
    articleAuthor,
    typeTags,
    furtherContentsTeaser,
    crossTeaserContainer,
    thinkingSpaces,
    widgetUrl,
    shopUrl,
    registerable,
    bookable,
    seatsAvailable
  } = data;
  const t = useTranslations();
  let jsonld = null;

  if (type === 'article') {
    jsonld = articleJSONLD(data);
  } else if (type === 'event') {
    jsonld = eventJSONLD(data);
  }

  const ticketShopLabel = t('accordion.ticketShopLabel');

  return (
    <TemplateDefault status={data.status} jsonld={jsonld}>
      <Stage
        showDate={type === 'article' || type === 'pressKit'}
        shopText={ticketShopLabel}
        shopUrl={shopUrl && (registerable || bookable) && seatsAvailable > 0 ? shopUrl : null}
      />
      <ContentBlocks
        noShareLinks={type === 'staticPage' || type === 'pressKit' || type === 'exhibition'}
      />
      {(registerable || bookable) && (
        <Container>
          {widgetUrl && (
            <Accordion
              onChange={items => {
                if (items.length > 0) {
                  track(['trackEvent', 'Booking', 'Click', t('accordion.bookTickets')]);
                }
              }}
              accordion={{
                sections: [
                  {
                    subline: t('accordion.bookTickets'),
                    type: 'iframe',
                    data: {
                      title: 'Ticket widget',
                      width: 800,
                      height: 600,
                      file: widgetUrl
                    }
                  }
                ]
              }}
            />
          )}

          {shopUrl && seatsAvailable > 0 && (
            <Link
              href={shopUrl}
              title={ticketShopLabel}
              className="gomusShopLink"
              rel="noopener noreferrer"
            >
              <Button onClick={() => track(['trackEvent', 'Booking', 'Click', ticketShopLabel])}>
                {ticketShopLabel}
              </Button>
            </Link>
          )}
        </Container>
      )}
      {articleAuthor &&
        articleAuthor.map(author => <Author {...author} key={author.id || author.fullName} />)}
      {typeTags && <TagList headline="Mehr zum Thema" tags={typeTags} />}
      {thinkingSpaces && thinkingSpaces.length > 0 && <TeaserContainer data={thinkingSpaces} />}
      {furtherContentsTeaser && (
        <div className="crossTeaser__container">
          <TeaserContainer data={furtherContentsTeaser.data} />
        </div>
      )}
      {crossTeaserContainer && (
        <div className="crossTeaser__container">
          <CrossTeasers data={crossTeaserContainer.crossTeaser} />
          <TeaserContainer data={crossTeaserContainer.basicTeaser} />
        </div>
      )}
    </TemplateDefault>
  );
};

Entry.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string.isRequired,
    articleAuthor: PropTypes.arrayOf(PropTypes.shape()),
    widgetUrl: PropTypes.string,
    shopUrl: PropTypes.string,
    typeTags: PropTypes.arrayOf(PropTypes.shape()),
    furtherContentsTeaser: PropTypes.arrayOf(PropTypes.shape()),
    crossTeaserContainer: PropTypes.shape({
      crossTeaser: PropTypes.arrayOf(PropTypes.shape()),
      basicTeaser: PropTypes.arrayOf(PropTypes.shape())
    }),
    thinkingSpaces: PropTypes.arrayOf(PropTypes.shape()),
    registerable: PropTypes.bool,
    bookable: PropTypes.bool,
    seatsAvailable: PropTypes.number,
    status: PropTypes.string
  })
};

export default Entry;
